import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Search from "@insite/mobius/Icons/Search";
import OverflowMenu, { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useState } from "react";
import styled, { css } from "styled-components";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import Icon from "@insite/mobius/Icon";

interface OwnProps {
    searchText?: string;
    onSearchTextChanged: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onSearch: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    allowCreateAddress?: boolean;
    onCreateNewAddressClick?: React.EventHandler<React.MouseEvent>;
    isSearchDisabled?: boolean;
    extendedStyles?: CustomerSelectorToolbarStyles;
}

export interface CustomerSelectorToolbarStyles {
    wrapper?: InjectableCss;
    searchTextField?: TextFieldProps;
    searchButton?: ButtonPresentationProps;
    wideHidden?: HiddenProps;

    narrowHidden?: HiddenProps;
    overflowMenu?: OverflowMenuPresentationProps;
    narrowCreateNewAddressClickable?: ClickablePresentationProps;
    wideCreateNewAddressButton?: InjectableCss;
    searchWrapper?: InjectableCss;  
    buttonWrapper?: InjectableCss;  
    selectAddressButton?: InjectableCss;  
}

export const customerSelectorToolbarStyles: CustomerSelectorToolbarStyles = {
    wrapper: {
        css: css`
            display: flex;
            border-bottom: 1px solid #C2D1D9;
            padding-bottom: 25px;
            position: relative;
            flex-direction: column;
            
            label {
                color: #2E384D;
                font-family: Roboto;
                font-size: 14px;
                letter-spacing: 0.16px;
                line-height: 40px;
                margin-right: 10px;
            }

            .border-seperator {
                border-bottom: 1px solid #C2D1D9;
                padding-top: 0;
                margin-bottom: 20px;
                width: 100%;
                display: block;
            }
        `,
    },
    searchTextField: {
        cssOverrides: {
            formField: css`
                flex-basis: 367px;
            `,
        },
    },
    searchButton: {
        variant: "primary",
        css: css`
            margin-left: auto;
            background: none;
            border: none;
            position: absolute;
            padding: 10px 5px;
            left: 385px;
            top: -2px;

            svg {
                height: 20px;
                width: 20px;
            }

            &:hover,
            &:active, 
            &:focus {
                background: none;
                border: none;
            }

            @media screen and (max-width: 767px) {
                left: auto;
                right: 10px;
            }
        `,
    },
    wideHidden: { below: "md" },
    wideCreateNewAddressButton: {
        css: css`
            margin-left: 0;
            flex-basis: 50%;
            max-width: 50%;
            margin-bottom: 0;
            width: 50%;
            color: #002554 !important;
            font-family: Roboto;
            font-size: 14px;
            font-weight: 500;
            letter-spacing: 0;
            line-height: 19px;
            text-align: center;
            padding: 10px 0;
            display: inline-block;
            background: none !important;
            border: none !important;
            cursor: pointer;
            vertical-align: middle;
            span {
                font-size: 14px;
                font-weight: 500;
            }
            &.current {
                color: #fff !important;
                background-color: #2060AD !important;

                svg {
                    color: #fff !important; 
                }
            }

            @media (max-width: 767px) {
                height: auto;
                text-align: left;
                padding-left: 15px;
                position: relative;

                .text {
                    max-width: 110px;
                    display: inline-block;
                    vertical-align: bottom;
                    text-align: left;
                }

                .arrows {
                    display: inline-block;
                    vertical-align: bottom;
                    line-height: 16px;
                    height: 18px;
                    position: absolute;
                    right: 15px;
                    bottom: 10px;
                }

                .up-arrow {
                    display: none;
                }


                &.current {
                    .down-arrow {
                        display: none;
                    }

                    .up-arrow {
                        display: inline-block;
                    }
                }
            }
            
        `,
    },
    selectAddressButton: {
        css: css`
            width: 50%;
            flex-basis: 50%;
            max-width: 50%;
            margin-bottom: 0;
            width: 50%;
            color: #002554 !important;
            font-family: Roboto;
            font-size: 14px;
            font-weight: 500;
            letter-spacing: 0;
            line-height: 19px;
            text-align: center;
            padding: 10px 0;
            display: inline-block;
            background: none !important;
            border: none !important;
            cursor: pointer;
            vertical-align: middle;
            span {
                font-size: 14px;
                font-weight: 500;
            }
            &.current {
                color: #fff !important;
                background-color: #2060AD !important;

                svg {
                    color: #fff !important; 
                }
            }

            @media (max-width: 767px) {
                height: auto;
                text-align: left;
                padding-left: 15px;
                position: relative;

                .text {
                    max-width: 110px;
                    display: inline-block;
                    vertical-align: bottom;
                    text-align: left;
                }

                .arrows {
                    display: inline-block;
                    vertical-align: bottom;
                    line-height: 16px;
                    height: 18px;
                    position: absolute;
                    right: 15px;
                    bottom: 10px;
                }

                .up-arrow {
                    display: none;
                }


                &.current {
                    .down-arrow {
                        display: none;
                    }

                    .up-arrow {
                        display: inline-block;
                    }
                }
            }
        `,
    },
    narrowHidden: { above: "sm" },
    overflowMenu: {
        cssOverrides: {
            wrapper: css`
                margin-left: 10px;
            `,
        },
    },
    searchWrapper: {
        css: css`
            width: 100%;
            flex-basis: 100%;
            max-width: 100%;
            position: relative;
            flex-direction: row;
            display: flex;
        `,
    },
    buttonWrapper: {
        css: css`
            width: 100%;
            flex-basis: 100%;
            max-width: 100%;
            border: 1px solid #C3D4EA;
            border-radius: 2px;
            background-color: #F3F8FE;
            margin-bottom: 20px;
            padding: 4px 7px;
        `,
    }
};

const ToolbarStyled = styled.div<InjectableCss>`
    ${({ css }) => css}
`;

const CustomerSelectorToolbar: FC<OwnProps> = ({
    allowCreateAddress = false,
    onCreateNewAddressClick,
    extendedStyles,
    searchText,
    onSearchTextChanged,
    onSearch,
    isSearchDisabled,
}) => {
    const [styles] = useState(() => mergeToNew(customerSelectorToolbarStyles, extendedStyles));

    return (
        <ToolbarStyled {...styles.wrapper}>
            {/* <StyledWrapper {...styles.buttonWrapper}>
                <Button
                    {...styles.selectAddressButton}
                    data-test-selector="customerSelectorToolbar_selectNewAddress"
                    className="current"
                >
                    <StyledWrapper as="span" className="text">{translate("Select a shipping address")}</StyledWrapper>
                    <Hidden above="sm">
                        <StyledWrapper as="span" className="arrows">
                        <Icon className="up-arrow" src="ChevronUp" size={16} />
                        <Icon className="down-arrow" src="ChevronDown" size={16} />
                    </StyledWrapper>
                    </Hidden>
                </Button>
            { {allowCreateAddress && onCreateNewAddressClick &&    (
                <>
                    <Button
                            {...styles.wideCreateNewAddressButton}
                            onClick={onCreateNewAddressClick}
                            data-test-selector="customerSelectorToolbar_createNewAddress"
                        >
                            <StyledWrapper as="span" className="text">{translate("Add New Address")}</StyledWrapper>
                            <Hidden above="sm">
                                <StyledWrapper as="span" className="arrows">
                                    <Icon className="up-arrow" src="ChevronUp" size={16} />
                                    <Icon className="down-arrow" src="ChevronDown" size={16} />
                                </StyledWrapper>
                            </Hidden>
                    </Button>
                </>
            )}
            </StyledWrapper> */}
            <StyledWrapper {...styles.searchWrapper}>
                <label htmlFor="searchAddress">Search</label>
                <TextField
                    {...styles.searchTextField}
                    placeholder="Search Addresses"
                    value={searchText}
                    onChange={onSearchTextChanged}
                    id="searchAddress"
                />
                <Button {...styles.searchButton} onClick={onSearch} disabled={isSearchDisabled}>
                    <Icon src={Search} />
                    <VisuallyHidden>{translate("Search")}</VisuallyHidden>
                </Button>
                </StyledWrapper>
        </ToolbarStyled>
    );
};

export default CustomerSelectorToolbar;
